/**
 * Offline outbox for sales-order submissions.
 *
 * The field-use contract (CLAUDE.md): a tech with no signal can build a quote,
 * hit submit, and the system queues it locally. When the device reconnects, the
 * outbox drains. This file is the storage layer; the retry loop lives in
 * outbox-processor.ts.
 *
 * Each row carries:
 *   - entryId: a stable client-side UUID (different from idempotencyKey).
 *   - idempotencyKey: forwarded on every attempt as the Idempotency-Key header.
 *     Reusing the same key across retries is the whole point — Spire's audit
 *     log carries it even when Spire itself doesn't dedupe.
 *   - body: the JSON we'd send to POST /api/v1/sales-orders.
 *   - status: pending | sending | failed | sent. 'sent' rows are kept briefly
 *     for UI feedback, then garbage-collected.
 *   - attempts, lastError: diagnostics surfaced in the /outbox screen.
 */

import Dexie, { type EntityTable } from 'dexie';
import type { CreateSalesOrderBody } from './api.js';

export type OutboxStatus = 'pending' | 'sending' | 'failed' | 'sent';

// ─── Field-event entries (audit follow-up) ───────────────────────────────
// Field-tech captures (parts / photos / notes / signature) need to survive
// a refresh and drain on reconnect to honour CLAUDE.md's offline contract.
// Until now they were fire-and-forget against the API: success showed a
// green check, failure showed a red X with a retry button — but ONLY
// while the page stayed open. Refresh wiped the local state; photos lost
// their base64 entirely (sessionStorage strip in field-job.ts).
//
// Each capture is a `field-event` row drained by the same processor that
// drains the sales-order rows above, with the same retry policy. The
// payload shape is tagged by `kind` so the processor can dispatch the
// right `api.*` call.

export type FieldEventKind = 'addItem' | 'addPhoto' | 'addNote' | 'attachSignature';

export interface FieldEventPayloadAddItem {
  whse: string;
  partNo: string;
  orderQty: string;
  unitPrice?: string;
  description?: string;
}

export interface FieldEventPayloadAddPhoto {
  contentType: 'image/png' | 'image/jpeg';
  base64: string;
  filename?: string;
  caption?: string;
}

export interface FieldEventPayloadAddNote {
  body: string;
  subject?: string;
}

export interface FieldEventPayloadAttachSignature {
  contentType: 'image/png' | 'image/jpeg';
  base64: string;
  filename?: string;
  subject?: string;
}

export type FieldEventPayload =
  | { kind: 'addItem'; data: FieldEventPayloadAddItem }
  | { kind: 'addPhoto'; data: FieldEventPayloadAddPhoto }
  | { kind: 'addNote'; data: FieldEventPayloadAddNote }
  | { kind: 'attachSignature'; data: FieldEventPayloadAttachSignature };

export interface FieldEventEntry {
  entryId: string;
  /** Spire sales-order spireId the event acts on. */
  spireSalesOrderId: number;
  /** Order number for UI labelling. Not a lookup key. */
  spireOrderNo: string;
  payload: FieldEventPayload;
  status: OutboxStatus;
  attempts: number;
  createdAt: number;
  updatedAt: number;
  lastError: string | null;
  /** Spire-side result id (lineId for addItem, noteId for the rest). */
  spireResultId: number | null;
  /** Stable client-side id reused as the idempotency key on retries. */
  idempotencyKey: string;
  /** Optional zustand-side correlation id so the local UI can mirror
   * status changes back into the in-memory action list. */
  localClientId: string | null;
}

/**
 * Optional customer signature attached to the entry. When present, the
 * processor uploads it to /api/v1/sales-orders/{spireId}/signature AFTER
 * the order POST succeeds. If the order succeeds but the signature step
 * fails, the entry stays as 'pending' with `lastSpireId`/`lastOrderNo` set
 * — the next drain skips the order create and just retries the signature.
 */
export interface OutboxSignature {
  contentType: 'image/png' | 'image/jpeg';
  base64: string;
}

/** A photo the rep attached while building the quote. */
export interface OutboxQuotePhoto {
  contentType: 'image/png' | 'image/jpeg';
  base64: string;
  filename: string;
}

/**
 * A quote-draft photo waiting for submit. Lives in IndexedDB rather than the
 * localStorage-persisted draft: a handful of base64 photos would blow
 * localStorage's ~5 MB quota, and the draft must survive a refresh / iOS
 * tab eviction (audit P0-11).
 */
export interface QuoteDraftPhoto extends OutboxQuotePhoto {
  clientId: string;
  createdAt: number;
}

export interface SalesOrderOutboxEntry {
  entryId: string;
  idempotencyKey: string;
  body: CreateSalesOrderBody;
  /** Optional signature to upload after the order is created. */
  signature: OutboxSignature | null;
  /**
   * Photos + written note captured on the quote. Once the order exists the
   * processor fans them out into `fieldEventOutbox` (addPhoto / addNote)
   * — which already has per-item retry + outbox UI — and sets
   * `extrasEnqueued` so a retried entry never fans out twice. Optional:
   * entries queued by older bundles don't carry them.
   */
  photos?: OutboxQuotePhoto[];
  note?: string | null;
  /** CRM-note subject for `note`; defaults to the quote subject. Field-created jobs use 'Tinsmith field note'. */
  noteSubject?: string;
  extrasEnqueued?: boolean;
  status: OutboxStatus;
  attempts: number;
  createdAt: number;
  updatedAt: number;
  lastError: string | null;
  /** When the order POST has succeeded, the orderNo Spire returned. */
  lastOrderNo: string | null;
  /** When the order POST has succeeded, the order's spireId for deep-link rendering. */
  lastSpireId: number | null;
  /** When the signature upload has succeeded, the CRM note id. */
  signatureNoteId: number | null;
}

// ─── Dexie database ───────────────────────────────────────────────────────

type TinsmithDB = Dexie & {
  salesOrderOutbox: EntityTable<SalesOrderOutboxEntry, 'entryId'>;
  fieldEventOutbox: EntityTable<FieldEventEntry, 'entryId'>;
  quoteDraftPhotos: EntityTable<QuoteDraftPhoto, 'clientId'>;
};

export function createOutboxDb(name = 'tinsmith'): TinsmithDB {
  const db = new Dexie(name) as TinsmithDB;
  db.version(1).stores({
    salesOrderOutbox: 'entryId, status, createdAt',
  });
  // v2 — field-event store added for parts/photos/notes/signatures so
  // captures survive a refresh + drain on reconnect. Existing
  // salesOrderOutbox rows are preserved as-is (Dexie migrates in place).
  db.version(2).stores({
    salesOrderOutbox: 'entryId, status, createdAt',
    fieldEventOutbox: 'entryId, status, createdAt, spireSalesOrderId',
  });
  // v3 — photos attached to the in-progress quote draft (see QuoteDraftPhoto).
  db.version(3).stores({
    salesOrderOutbox: 'entryId, status, createdAt',
    fieldEventOutbox: 'entryId, status, createdAt, spireSalesOrderId',
    quoteDraftPhotos: 'clientId, createdAt',
  });
  return db;
}

let dbInstance: TinsmithDB | null = null;

export function getOutboxDb(): TinsmithDB {
  if (!dbInstance) dbInstance = createOutboxDb();
  return dbInstance;
}

/** Reset the singleton — used by tests, not by app code. */
export function _resetOutboxDb(): void {
  dbInstance = null;
}

// ─── Actions ──────────────────────────────────────────────────────────────

export interface EnqueueInput {
  entryId: string;
  idempotencyKey: string;
  body: CreateSalesOrderBody;
  signature?: OutboxSignature | null;
  photos?: OutboxQuotePhoto[];
  note?: string | null;
  noteSubject?: string;
}

export async function enqueueSalesOrder(
  input: EnqueueInput,
  db: TinsmithDB = getOutboxDb(),
): Promise<SalesOrderOutboxEntry> {
  const now = Date.now();
  const entry: SalesOrderOutboxEntry = {
    entryId: input.entryId,
    idempotencyKey: input.idempotencyKey,
    body: input.body,
    signature: input.signature ?? null,
    photos: input.photos ?? [],
    note: input.note?.trim() ? input.note.trim() : null,
    ...(input.noteSubject ? { noteSubject: input.noteSubject } : {}),
    extrasEnqueued: false,
    status: 'pending',
    attempts: 0,
    createdAt: now,
    updatedAt: now,
    lastError: null,
    lastOrderNo: null,
    lastSpireId: null,
    signatureNoteId: null,
  };
  await db.salesOrderOutbox.put(entry);
  return entry;
}

export async function markSending(
  entryId: string,
  db: TinsmithDB = getOutboxDb(),
): Promise<void> {
  await db.salesOrderOutbox.update(entryId, {
    status: 'sending',
    updatedAt: Date.now(),
  });
}

export async function markSent(
  entryId: string,
  result: { orderNo: string; spireId: number },
  db: TinsmithDB = getOutboxDb(),
): Promise<void> {
  await db.salesOrderOutbox.update(entryId, {
    status: 'sent',
    lastOrderNo: result.orderNo,
    lastSpireId: result.spireId,
    lastError: null,
    updatedAt: Date.now(),
  });
}

/** Record that the order create step succeeded but the entry still has work (signature). */
export async function markOrderCreated(
  entryId: string,
  result: { orderNo: string; spireId: number },
  db: TinsmithDB = getOutboxDb(),
): Promise<void> {
  await db.salesOrderOutbox.update(entryId, {
    lastOrderNo: result.orderNo,
    lastSpireId: result.spireId,
    lastError: null,
    updatedAt: Date.now(),
  });
}

/** Record that the entry's photos + note were fanned out into fieldEventOutbox. */
export async function markExtrasEnqueued(
  entryId: string,
  db: TinsmithDB = getOutboxDb(),
): Promise<void> {
  // Drop the photo bytes too — they now live on the field-event rows.
  await db.salesOrderOutbox.update(entryId, {
    extrasEnqueued: true,
    photos: [],
    updatedAt: Date.now(),
  });
}

/** Record that the signature upload succeeded. */
export async function markSignatureAttached(
  entryId: string,
  noteId: number,
  db: TinsmithDB = getOutboxDb(),
): Promise<void> {
  await db.salesOrderOutbox.update(entryId, {
    signatureNoteId: noteId,
    updatedAt: Date.now(),
  });
}

export async function markFailed(
  entryId: string,
  error: string,
  options: { permanent: boolean },
  db: TinsmithDB = getOutboxDb(),
): Promise<void> {
  const existing = await db.salesOrderOutbox.get(entryId);
  if (!existing) return;
  await db.salesOrderOutbox.update(entryId, {
    status: options.permanent ? 'failed' : 'pending',
    attempts: existing.attempts + 1,
    lastError: error,
    updatedAt: Date.now(),
  });
}

export async function retryEntry(
  entryId: string,
  db: TinsmithDB = getOutboxDb(),
): Promise<void> {
  await db.salesOrderOutbox.update(entryId, {
    status: 'pending',
    lastError: null,
    updatedAt: Date.now(),
  });
}

export async function removeEntry(
  entryId: string,
  db: TinsmithDB = getOutboxDb(),
): Promise<void> {
  await db.salesOrderOutbox.delete(entryId);
}

/** List entries for the UI. Pending first, then failed, then most-recent sent. */
export async function listEntries(
  db: TinsmithDB = getOutboxDb(),
): Promise<SalesOrderOutboxEntry[]> {
  const rows = await db.salesOrderOutbox.orderBy('createdAt').toArray();
  return rows.sort((a, b) => statusRank(a.status) - statusRank(b.status));
}

/** Pending or sending — the ones the processor wants to drain. */
export async function listDrainable(
  db: TinsmithDB = getOutboxDb(),
): Promise<SalesOrderOutboxEntry[]> {
  return db.salesOrderOutbox
    .where('status')
    .anyOf(['pending', 'sending'])
    .toArray();
}

export async function countPending(
  db: TinsmithDB = getOutboxDb(),
): Promise<number> {
  return db.salesOrderOutbox.where('status').anyOf(['pending', 'sending', 'failed']).count();
}

/** Garbage-collect 'sent' entries older than `olderThanMs`. */
export async function pruneSent(
  olderThanMs: number,
  db: TinsmithDB = getOutboxDb(),
): Promise<number> {
  const cutoff = Date.now() - olderThanMs;
  const sent = await db.salesOrderOutbox
    .where('status')
    .equals('sent')
    .toArray();
  const stale = sent.filter((e) => e.updatedAt < cutoff);
  if (stale.length === 0) return 0;
  await db.salesOrderOutbox.bulkDelete(stale.map((e) => e.entryId));
  return stale.length;
}

function statusRank(s: OutboxStatus): number {
  switch (s) {
    case 'sending':
      return 0;
    case 'pending':
      return 1;
    case 'failed':
      return 2;
    case 'sent':
      return 3;
  }
}

export function generateEntryId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
}

// ─── Field-event helpers ──────────────────────────────────────────────────

/**
 * True for errors worth queuing for later retry: network failures (no
 * response) and 5xx responses (server transient). 4xx responses are NOT
 * queued — they're caller-side problems (validation, auth, lock conflict,
 * etc.) that retrying won't fix.
 *
 * Callers can pass any thrown value; we narrow internally.
 */
export function shouldQueueFieldEvent(err: unknown): boolean {
  if (err === null || typeof err !== 'object') return false;
  const maybe = err as { status?: unknown; kind?: unknown };
  if (maybe.kind === 'network') return true;
  if (typeof maybe.status === 'number') {
    // 423 = order locked in Spire desktop: queue it, it clears on its own.
    return maybe.status === 0 || maybe.status >= 500 || maybe.status === 423;
  }
  return false;
}


export interface EnqueueFieldEventInput {
  entryId?: string;
  idempotencyKey?: string;
  spireSalesOrderId: number;
  spireOrderNo: string;
  payload: FieldEventPayload;
  localClientId?: string | null;
  /**
   * Drain-order key (defaults to now). Pass increasing values when queueing
   * a batch in one tick so the rows keep their order — same-millisecond
   * rows otherwise sort arbitrarily.
   */
  createdAt?: number;
}

export async function enqueueFieldEvent(
  input: EnqueueFieldEventInput,
  db: TinsmithDB = getOutboxDb(),
): Promise<FieldEventEntry> {
  const now = Date.now();
  const entryId = input.entryId ?? generateEntryId();
  const entry: FieldEventEntry = {
    entryId,
    spireSalesOrderId: input.spireSalesOrderId,
    spireOrderNo: input.spireOrderNo,
    payload: input.payload,
    status: 'pending',
    attempts: 0,
    createdAt: input.createdAt ?? now,
    updatedAt: now,
    lastError: null,
    spireResultId: null,
    idempotencyKey: input.idempotencyKey ?? entryId,
    localClientId: input.localClientId ?? null,
  };
  await db.fieldEventOutbox.put(entry);
  return entry;
}

export async function markFieldEventSending(
  entryId: string,
  db: TinsmithDB = getOutboxDb(),
): Promise<void> {
  await db.fieldEventOutbox.update(entryId, {
    status: 'sending',
    updatedAt: Date.now(),
  });
}

export async function markFieldEventSent(
  entryId: string,
  spireResultId: number,
  db: TinsmithDB = getOutboxDb(),
): Promise<void> {
  await db.fieldEventOutbox.update(entryId, {
    status: 'sent',
    spireResultId,
    lastError: null,
    updatedAt: Date.now(),
  });
}

export async function markFieldEventFailed(
  entryId: string,
  error: string,
  options: { permanent: boolean },
  db: TinsmithDB = getOutboxDb(),
): Promise<void> {
  const existing = await db.fieldEventOutbox.get(entryId);
  if (!existing) return;
  await db.fieldEventOutbox.update(entryId, {
    status: options.permanent ? 'failed' : 'pending',
    attempts: existing.attempts + 1,
    lastError: error,
    updatedAt: Date.now(),
  });
}

export async function retryFieldEvent(
  entryId: string,
  db: TinsmithDB = getOutboxDb(),
): Promise<void> {
  await db.fieldEventOutbox.update(entryId, {
    status: 'pending',
    lastError: null,
    updatedAt: Date.now(),
  });
}

export async function removeFieldEvent(
  entryId: string,
  db: TinsmithDB = getOutboxDb(),
): Promise<void> {
  await db.fieldEventOutbox.delete(entryId);
}

export async function listDrainableFieldEvents(
  db: TinsmithDB = getOutboxDb(),
): Promise<FieldEventEntry[]> {
  // Oldest first — the status index alone yields rows in (random) entryId
  // order, which shuffled a batch queued together (e.g. a quote's photos).
  return db.fieldEventOutbox
    .where('status')
    .anyOf(['pending', 'sending'])
    .sortBy('createdAt');
}

export async function listFieldEvents(
  db: TinsmithDB = getOutboxDb(),
): Promise<FieldEventEntry[]> {
  const rows = await db.fieldEventOutbox.orderBy('createdAt').toArray();
  return rows.sort((a, b) => statusRank(a.status) - statusRank(b.status));
}

/** Field events not-yet-synced (pending | sending | failed). Surfaces a
 * red badge on the Outbox tab. */
export async function countPendingFieldEvents(
  db: TinsmithDB = getOutboxDb(),
): Promise<number> {
  return db.fieldEventOutbox
    .where('status')
    .anyOf(['pending', 'sending', 'failed'])
    .count();
}

export async function pruneSentFieldEvents(
  olderThanMs: number,
  db: TinsmithDB = getOutboxDb(),
): Promise<number> {
  const cutoff = Date.now() - olderThanMs;
  const sent = await db.fieldEventOutbox.where('status').equals('sent').toArray();
  const stale = sent.filter((e) => e.updatedAt < cutoff);
  if (stale.length === 0) return 0;
  await db.fieldEventOutbox.bulkDelete(stale.map((e) => e.entryId));
  return stale.length;
}

// ─── Quote-draft photos ───────────────────────────────────────────────────

export async function addQuoteDraftPhoto(
  photo: OutboxQuotePhoto,
  db: TinsmithDB = getOutboxDb(),
): Promise<QuoteDraftPhoto> {
  const row: QuoteDraftPhoto = { ...photo, clientId: generateEntryId(), createdAt: Date.now() };
  await db.quoteDraftPhotos.put(row);
  return row;
}

export async function listQuoteDraftPhotos(
  db: TinsmithDB = getOutboxDb(),
): Promise<QuoteDraftPhoto[]> {
  return db.quoteDraftPhotos.orderBy('createdAt').toArray();
}

export async function removeQuoteDraftPhoto(
  clientId: string,
  db: TinsmithDB = getOutboxDb(),
): Promise<void> {
  await db.quoteDraftPhotos.delete(clientId);
}

export async function clearQuoteDraftPhotos(db: TinsmithDB = getOutboxDb()): Promise<void> {
  await db.quoteDraftPhotos.clear();
}
